import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { NAV_LINKS } from "../data/siteData";
import { useSiteConfig } from "../hooks/useSiteConfig";
import LanguageSwitcher from "./LanguageSwitcher";

export default function NavBar() {
  const { general } = useSiteConfig();
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", h);
    return () => window.removeEventListener("scroll", h);
  }, []);

  const linkStyle = (isActive) => ({
    background: "none",
    border: "none",
    cursor: "pointer",
    fontFamily: "var(--font-body)",
    fontSize: 12.5,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    textDecoration: "none",
    color: isActive ? "var(--color-accent)" : "var(--text-subtle)",
    transition: "color 0.3s",
    padding: "4px 0",
    borderBottom: isActive
      ? "1.5px solid var(--color-accent)"
      : "1.5px solid transparent",
  });

  return (
    <nav
      role="navigation"
      aria-label={t("a11y.mainNav")}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        background: scrolled ? "rgba(8,8,12,0.94)" : "transparent",
        backdropFilter: scrolled ? "blur(24px)" : "none",
        borderBottom: scrolled ? "1px solid var(--accent-06)" : "none",
        transition: "all 0.5s var(--ease-out)",
      }}
    >
      <div
        style={{
          maxWidth: 1100,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          height: 64,
          padding: "0 28px",
        }}
      >
        {/* Logo */}
        <Link
          to="/"
          onClick={() => setMenuOpen(false)}
          style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none" }}
          aria-label={t("a11y.homeLogo")}
        >
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 8,
              background: "linear-gradient(135deg, var(--color-accent) 0%, var(--color-accent-deeper) 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "var(--font-display)",
              fontSize: 14,
              fontWeight: 700,
              color: "#080810",
            }}
          >
            {general.initials}
          </div>
          <span
            className="cb-nav-name"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: 17,
              color: "var(--color-text)",
            }}
          >
            {general.shortName}
          </span>
        </Link>

        {/* Desktop Nav */}
        <div className="cb-desktop-nav" style={{ display: "flex", gap: 28, alignItems: "center" }}>
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              end={l.path === "/"}
              style={({ isActive }) => linkStyle(isActive)}
            >
              {t(l.labelKey)}
            </NavLink>
          ))}
          <LanguageSwitcher />
        </div>

        {/* Mobile buttons */}
        <div className="cb-mobile-btn" style={{ display: "none", alignItems: "center", gap: 8 }}>
          <LanguageSwitcher />
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? t("a11y.closeMenu") : t("a11y.openMenu")}
            aria-expanded={menuOpen}
            style={{
              background: "none",
              border: "none",
              cursor: "pointer",
              color: "var(--color-accent)",
              fontSize: 22,
              padding: 4,
            }}
          >
            {menuOpen ? "\u2715" : "\u2630"}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          style={{
            padding: "12px 28px 24px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            background: "rgba(8,8,12,0.97)",
            borderBottom: "1px solid var(--accent-08)",
          }}
        >
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.path}
              to={l.path}
              end={l.path === "/"}
              onClick={() => setMenuOpen(false)}
              style={({ isActive }) => ({
                textDecoration: "none",
                textAlign: "left",
                fontFamily: "var(--font-body)",
                fontSize: 14,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: isActive ? "var(--color-accent)" : "rgba(234,234,234,0.5)",
                padding: "10px 0",
                borderBottom: "1px solid rgba(234,234,234,0.04)",
              })}
            >
              {t(l.labelKey)}
            </NavLink>
          ))}
        </div>
      )}
    </nav>
  );
}
